/**
 * Fetches the product recommendations section HTML (see Shopify Ajax API:
 * GET /{locale}/recommendations/products?product_id=&section_id=&limit=&intent=)
 * Replaces list or carousel placeholders when recommendations load.
 */
if (typeof ProductRecommendations === "undefined") {
	class ProductRecommendations extends HTMLElement {
		constructor() {
			super();
			this.isLoaded = false;
		}

		getInjectionTarget() {
			return this.querySelector("ul") || this.querySelector(".swiper-wrapper");
		}

		hasRealPairingItems(container) {
			if (!container) return false;

			const children = [...container.children];

			if (container.matches("ul")) {
				return children.some(
					(li) =>
						!li.classList.contains("skeleton-list-item") &&
						li.querySelector("[data-product-card-link], product-card")
				);
			}

			return children.some(
				(slide) =>
					!slide.classList.contains("skeleton-slide") &&
					slide.querySelector("[data-product-card-link], product-card")
			);
		}

		updateCarousel() {
			const carousel = this.querySelector("best-sellers-carousel");
			if (!carousel?.swiper) return;

			requestAnimationFrame(() => {
				carousel.swiper.update();
				if (typeof carousel.swiper.updateAutoHeight === "function") {
					carousel.swiper.updateAutoHeight(0);
				}
			});
		}

		replaceContainerChildren(container, items) {
			if (!items.length) return;

			const hasSkeletons = container.querySelector(
				container.matches("ul") ? ".skeleton-list-item" : ".skeleton-slide"
			);

			if (hasSkeletons) {
				container.replaceChildren(...items);
			} else {
				container
					.querySelectorAll(
						container.matches("ul") ? ".skeleton-list-item" : ".skeleton-slide"
					)
					.forEach((el) => el.remove());
				const fragment = document.createDocumentFragment();
				items.forEach((item) => fragment.appendChild(item));
				container.appendChild(fragment);
			}

			if (container.matches(".swiper-wrapper")) {
				this.updateCarousel();
			}

			window.enableAtcButtonsAwaitingCart?.();
		}

		connectedCallback() {
			if (this.isLoaded) return;

			const url = this.dataset.url;
			if (!url) return;

			const container = this.getInjectionTarget();
			if (!container) return;

			// Metafield pairings rendered server-side — skip API
			if (this.hasAttribute("data-has-pairings") || this.hasRealPairingItems(container)) {
				this.isLoaded = true;
				window.enableAtcButtonsAwaitingCart?.();
				return;
			}

			fetch(url)
				.then((response) => {
					if (!response.ok) throw new Error(`HTTP ${response.status}`);
					return response.text();
				})
				.then((text) => {
					const wrapper = document.createElement("div");
					wrapper.innerHTML = text;

					const swiperWrapper = this.querySelector(".swiper-wrapper");
					const nextCarousel = wrapper.querySelector("[data-product-recommendations-ymal]");

					if (swiperWrapper && nextCarousel) {
						const slides = [...nextCarousel.querySelectorAll(":scope > .swiper-slide")];
						if (!slides.length) return;
						this.replaceContainerChildren(swiperWrapper, slides);
						return;
					}

					const next = wrapper.querySelector("ul");
					const ul = this.querySelector("ul");
					if (!next || !ul || !next.innerHTML.trim().length) return;

					const items = [...next.querySelectorAll(":scope > li")];
					if (!items.length) return;

					this.replaceContainerChildren(ul, items);
				})
				.catch((e) => {
					console.error("Error fetching product recommendations:", e);
				})
				.finally(() => {
					this.isLoaded = true;
				});
		}
	}

	if (!window.customElements.get("product-recommendations")) {
		window.customElements.define("product-recommendations", ProductRecommendations);
	}
}
